// src/Payroll_Management/PayRoll_Report/EmployeeTransferReport.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  Hash,
  CalendarDays,
  MapPin,
  User,
  FileDown,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const EmployeeTransferReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";     // make sure you store this in login
  const branchType = localStorage.getItem("branchType") || "HO"; // "HO" or others

  // meta / dropdowns
  const [metaLoading, setMetaLoading] = useState(false);
  const [fromSites, setFromSites] = useState([]);
  const [toSites, setToSites] = useState([]);
  const [transferCodes, setTransferCodes] = useState([]);

  // filters
  const [fromSiteId, setFromSiteId] = useState("Select");
  const [toSiteId, setToSiteId] = useState("Select");
  const [tranCodeId, setTranCodeId] = useState("Select");
  const [fromDate, setFromDate] = useState(""); // dd/MM/yyyy
  const [toDate, setToDate] = useState("");     // dd/MM/yyyy
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  // autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-31", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const miniBtn =
    `inline-flex items-center justify-center gap-2 px-4 py-2 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // -----------------------------
  // Meta
  // -----------------------------
  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing branchId / branchType in localStorage (login values).");
      return;
    }

    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-transfer-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        const d = res.data;

        setFromSites(d.fromSites || []);
        setToSites(d.toSites || []);
        setTransferCodes(d.transferCodes || []);

        // defaults
        const defFrom = d.defaultFromSiteId ? String(d.defaultFromSiteId) : "Select";
        setFromSiteId(defFrom);
        setToSiteId("Select");
        setTranCodeId("Select");
        setFromDate(d.defaultFromDate || "");
        setToDate(d.defaultToDate || "");
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed.");
    } finally {
      setMetaLoading(false);
    }
  };

  // -----------------------------
  // Data
  // -----------------------------
  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-transfer-report/data`, {
        params: {
          finYearId: Number(finYearId),
          fromSiteId,
          toSiteId,
          fromDate: fromDate.trim(),
          toDate: toDate.trim(),
          empCode: empCode.trim(),
          empName: empName.trim(),
          tranCodeId,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Fetch failed.");
    } finally {
      setLoading(false);
    }
  };

  // initial
  useEffect(() => {
    (async () => {
      await fetchMeta();
      // fetch list after meta so default dates exist
      setPage(1);
      // slight delay not needed; just call with 1
      await fetchList(1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // paging
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // -----------------------------
  // Search
  // -----------------------------
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchMeta();
    setPage(1);
    await fetchList(1);
  };

  // -----------------------------
  // Autocomplete (simple)
  // -----------------------------
  const fetchCodeSug = async (q) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-transfer-report/autocomplete/code`, {
        params: {
          finYearId: Number(finYearId),
          fromSiteId,
          branchType,
          q,
        },
      });
      if (res?.data?.success) setCodeSug(res.data.items || []);
    } catch {
      // silent
    }
  };

  const fetchNameSug = async (q) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-transfer-report/autocomplete/name`, {
        params: {
          finYearId: Number(finYearId),
          fromSiteId,
          branchType,
          q,
        },
      });
      if (res?.data?.success) setNameSug(res.data.items || []);
    } catch {
      // silent
    }
  };

  // debounce helper
  const useDebounce = (value, delay = 350) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
  };

  const empCodeDeb = useDebounce(empCode, 350);
  const empNameDeb = useDebounce(empName, 350);

  useEffect(() => {
    if (!empCodeDeb.trim()) {
      setCodeSug([]);
      return;
    }
    fetchCodeSug(empCodeDeb.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCodeDeb, fromSiteId]);

  useEffect(() => {
    if (!empNameDeb.trim()) {
      setNameSug([]);
      return;
    }
    fetchNameSug(empNameDeb.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empNameDeb, fromSiteId]);

  // -----------------------------
  // Print actions (placeholder URLs)
  // -----------------------------
  const handleRowPrint = (empid) => {
    if (!empid) return;
    // later: connect to your print page route
    window.open(`/PayRoll/EmployeeTransferReportPrint?empId=${encodeURIComponent(empid)}`, "_blank");
  };

  const handlePrintAll = () => {
    // later: connect to print-all route
    const qs = new URLSearchParams({
      fromSiteId: fromSiteId === "Select" ? "" : fromSiteId,
      toSiteId: toSiteId === "Select" ? "" : toSiteId,
      fromDate: fromDate || "",
      toDate: toDate || "",
      empCode: empCode || "",
      empName: empName || "",
      tranCodeId: tranCodeId === "Select" ? "" : tranCodeId,
    }).toString();

    window.open(`/PayRoll/EmployeeTransferReport_Print?${qs}`, "_blank");
  };

  const handleExcelExport = () => {
    // later: when you add node excel endpoint, wire here:
    toast.info("Excel export not wired yet (add Node /export-excel endpoint later).");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE TRANSFER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Transfer Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Print / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${(loading || metaLoading) ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrintAll} className={primaryBtn} disabled={loading || metaLoading}>
                <Printer className="w-4 h-4" />
                Print All
              </button>

              <button onClick={handleExcelExport} className={ghostBtn} disabled={loading || metaLoading}>
                <FileDown className="w-4 h-4" />
                Excel
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Transfer Code */}
              <div>
                <label className={labelBase}>Transfer Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={tranCodeId}
                    onChange={(e) => setTranCodeId(e.target.value)}
                    disabled={metaLoading}
                  >
                    <option value="Select">Select</option>
                    {transferCodes.map((t) => (
                      <option key={String(t.id)} value={String(t.id)}>
                        {t.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* From Site */}
              <div>
                <label className={labelBase}>From Site</label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={fromSiteId}
                    onChange={(e) => {
                      setFromSiteId(e.target.value);
                      // reset suggestions to avoid cross-branch confusion
                      setCodeSug([]);
                      setNameSug([]);
                    }}
                    disabled={metaLoading || String(branchType) !== "HO"} // non-HO fixed
                    title={String(branchType) !== "HO" ? "From Site fixed to your branch" : ""}
                  >
                    <option value="Select">Select</option>
                    {fromSites.map((b) => (
                      <option key={String(b.id)} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* To Site */}
              <div>
                <label className={labelBase}>To Site</label>
                <div className="mt-2 flex items-center gap-2">
                  <MapPin className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={toSiteId}
                    onChange={(e) => setToSiteId(e.target.value)}
                    disabled={metaLoading}
                  >
                    <option value="Select">Select</option>
                    {toSites.map((b) => (
                      <option key={String(b.id)} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Employee Code (autocomplete) */}
              <div>
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empCode}
                    onChange={(e) => {
                      setEmpCode(e.target.value);
                      setShowCodeSug(true);
                    }}
                    onFocus={() => setShowCodeSug(true)}
                    onBlur={() => setTimeout(() => setShowCodeSug(false), 150)}
                    placeholder="Type employee code..."
                    className={`pl-11 ${inputBase}`}
                  />

                  {showCodeSug && codeSug.length > 0 && (
                    <div
                      className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                        isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                      }`}
                    >
                      {codeSug.slice(0, 10).map((it, idx) => (
                        <button
                          type="button"
                          key={`${it}-${idx}`}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => {
                            setEmpCode(it);
                            setShowCodeSug(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"
                          }`}
                        >
                          {it}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Employee Name (autocomplete) */}
              <div>
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empName}
                    onChange={(e) => {
                      setEmpName(e.target.value);
                      setShowNameSug(true);
                    }}
                    onFocus={() => setShowNameSug(true)}
                    onBlur={() => setTimeout(() => setShowNameSug(false), 150)}
                    placeholder="Type employee name..."
                    className={`pl-11 ${inputBase}`}
                  />

                  {showNameSug && nameSug.length > 0 && (
                    <div
                      className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                        isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                      }`}
                    >
                      {nameSug.slice(0, 10).map((it, idx) => (
                        <button
                          type="button"
                          key={`${it}-${idx}`}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => {
                            setEmpName(it);
                            setShowNameSug(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "text-gray-200 hover:bg-white/10" : "text-gray-800 hover:bg-gray-50"
                          }`}
                        >
                          {it}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* From Date */}
              <div>
                <label className={labelBase}>From Date (dd/MM/yyyy)</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    placeholder="dd/MM/yyyy"
                    className={inputBase}
                  />
                </div>
              </div>

              {/* To Date */}
              <div>
                <label className={labelBase}>To Date (dd/MM/yyyy)</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    placeholder="dd/MM/yyyy"
                    className={inputBase}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 mt-6">
              <button type="submit" className={primaryBtn} disabled={loading || metaLoading}>
                <Search className="w-4 h-4" />
                Search
              </button>
              <button type="button" onClick={handleRefresh} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${(loading || metaLoading) ? "animate-spin" : ""}`} />
                Reset
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Transfer Date</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold">From Site</th>
                    <th className="px-4 py-4 text-center font-bold">To Site</th>
                    <th className="px-4 py-4 text-center font-bold">Transfer Type</th>
                    <th className="px-4 py-4 text-center font-bold">Status</th>
                    <th className="px-4 py-4 text-center font-bold">Transfer By</th>
                    <th className="px-4 py-4 text-center font-bold">Transfer Approval By</th>
                    <th className="px-4 py-4 text-center font-bold">Join By</th>
                    <th className="px-4 py-4 text-center font-bold">Print</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 13 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={13}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.empid ?? idx}-${idx}`}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.trdate}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.code}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.empname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.depnme}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.frombrnme}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.brnme}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.tpe}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.status}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee_Transby}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_approvalstatusby}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee_joinby}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button onClick={() => handleRowPrint(r.empid)} className={miniBtn}>
                            <Printer className="w-4 h-4" />
                            Print
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeTransferReport;
